import { tKey } from 'ember-intl';
import {
  type Kpi,
  KPIS,
  type MetricType,
  type TableKpi,
} from 'lumen/models/market-performance/data-item';
import {
  LOCAL_STORAGE_KEYS,
  type StorageKey,
} from 'lumen/services/local-storage';
import type { KpiCardName } from 'lumen/utils/market-snapshots/kpis';

import { isVariance, type Variance, VARIANCES } from './variance';

export const SDLY_COLUMNS = {
  SDLY: 'sdly',
  NET_SDLY: 'net_sdly',
} as const;

export type SdlyColumn = (typeof SDLY_COLUMNS)[keyof typeof SDLY_COLUMNS];

export function isSdlyColumn(value: unknown): value is SdlyColumn {
  return Object.values(SDLY_COLUMNS).includes(value as SdlyColumn);
}

export const FORECAST_COLUMNS = {
  BI: 'bi_forecast',
  USER: 'user_forecast',
  RMS: 'rms_forecast',
  FINANCE: 'finance_forecast',
} as const;

export type ForecastColumn =
  (typeof FORECAST_COLUMNS)[keyof typeof FORECAST_COLUMNS];

export function isForecastColumn(value: unknown): value is ForecastColumn {
  return Object.values(FORECAST_COLUMNS).includes(value as ForecastColumn);
}

export const GROUP_COLUMNS_BY = {
  CATEGORIES: 'categories',
  KPIS: 'kpis',
} as const;

export type GroupColumnsBy =
  (typeof GROUP_COLUMNS_BY)[keyof typeof GROUP_COLUMNS_BY];

export function isGroupColumnsBy(value: unknown): value is GroupColumnsBy {
  return Object.values(GROUP_COLUMNS_BY).includes(value as GroupColumnsBy);
}

export const GROUP_BY = {
  MONTH: 'month',
  PROPERTY: 'property',
} as const;

export type GroupBy = (typeof GROUP_BY)[keyof typeof GROUP_BY];

export function isGroupBy(value: unknown): value is GroupBy {
  return Object.values(GROUP_BY).includes(value as GroupBy);
}

export const SORT_ON = {
  ACTUALS: 'actuals',
  VARIANCE: 'variance',
} as const;

export type SortOn = (typeof SORT_ON)[keyof typeof SORT_ON];

export function isSortOn(value: unknown): value is SortOn {
  return Object.values(SORT_ON).includes(value as SortOn);
}

export const OVERVIEW_KPI_NAMES: readonly Kpi[] = [
  'occupancy',
  'rooms',
  'adr',
  'revenue',
];

// Revenue and RevPAR stand in for each other when a grouping offers only one.
export const COUNTERPART_KPIS: Partial<Record<Kpi, Kpi>> = {
  revenue: 'revpar',
  revpar: 'revenue',
};

export interface DataPreferences {
  forecastColumn: ForecastColumn;
  sdlyColumn: SdlyColumn;
}

export const DEFAULT_DATA_PREFERENCES: Readonly<DataPreferences> =
  Object.freeze({
    forecastColumn: FORECAST_COLUMNS.BI,
    sdlyColumn: SDLY_COLUMNS.SDLY,
  });

export function parseDataPreferences(json: unknown): DataPreferences {
  if (typeof json !== 'object' || json === null) {
    return DEFAULT_DATA_PREFERENCES;
  }

  const raw = json as Record<string, unknown>;

  return {
    forecastColumn: isForecastColumn(raw.forecastColumn)
      ? raw.forecastColumn
      : DEFAULT_DATA_PREFERENCES.forecastColumn,
    sdlyColumn: isSdlyColumn(raw.sdlyColumn)
      ? raw.sdlyColumn
      : DEFAULT_DATA_PREFERENCES.sdlyColumn,
  };
}

export interface TableLayoutPreferences {
  groupColumnsBy: GroupColumnsBy;
  sortOn: SortOn;
  variance: Variance;
}

export const DEFAULT_TABLE_LAYOUT_PREFERENCES: Readonly<TableLayoutPreferences> =
  Object.freeze({
    groupColumnsBy: GROUP_COLUMNS_BY.CATEGORIES,
    sortOn: SORT_ON.VARIANCE,
    variance: VARIANCES.PERCENTAGE,
  });

export function parseTableLayoutPreferences(
  json: unknown,
): TableLayoutPreferences {
  if (typeof json !== 'object' || json === null) {
    return DEFAULT_TABLE_LAYOUT_PREFERENCES;
  }

  const raw = json as Record<string, unknown>;

  return {
    groupColumnsBy: isGroupColumnsBy(raw.groupColumnsBy)
      ? raw.groupColumnsBy
      : DEFAULT_TABLE_LAYOUT_PREFERENCES.groupColumnsBy,
    sortOn: isSortOn(raw.sortOn)
      ? raw.sortOn
      : DEFAULT_TABLE_LAYOUT_PREFERENCES.sortOn,
    variance: isVariance(raw.variance)
      ? raw.variance
      : DEFAULT_TABLE_LAYOUT_PREFERENCES.variance,
  };
}

export const METRIC_TYPE_TRANSLATIONS: Record<MetricType, string> = {
  bi_forecast: tKey('general.bi_forecast'),
  budget: tKey('general.budget'),
  finance_forecast: tKey('market.snapshots.metric_type.finance_forecast'),
  ly: tKey('market.snapshots.metric_type.total_ly'),
  net_sdly: tKey('market.snapshots.metric_type.net_sdly'),
  otb: tKey('general.otb'),
  rms_forecast: tKey('market.snapshots.metric_type.rms_forecast'),
  sdly: tKey('market.snapshots.metric_type.sdly'),
  user_forecast: tKey('market.snapshots.metric_type.user_forecast'),
  'otb:pickup:1': tKey('market.snapshots.metric_type.otb_pickup_1'),
  'otb:pickup:7': tKey('market.snapshots.metric_type.otb_pickup_7'),
  'bi_forecast:pickup:1': tKey(
    'market.snapshots.metric_type.bi_forecast_pickup_1',
  ),
  'bi_forecast:pickup:7': tKey(
    'market.snapshots.metric_type.bi_forecast_pickup_7',
  ),
  transient: tKey('market.snapshots.metric_type.transient'),
  'transient:sdly': tKey('market.snapshots.metric_type.transient_sdly'),
  'transient:net_sdly': tKey('market.snapshots.metric_type.transient_net_sdly'),
  'transient:ly': tKey('market.snapshots.metric_type.transient_finals_ly'),
  group: tKey('market.snapshots.metric_type.group'),
  'group:sdly': tKey('market.snapshots.metric_type.group_sdly'),
  'group:net_sdly': tKey('market.snapshots.metric_type.group_net_sdly'),
  'group:ly': tKey('market.snapshots.metric_type.group_finals_ly'),
};

// Any metric type the KPIS values can be compared against.
export type ComparisonMetricType = Exclude<MetricType, 'otb'>;

// Reorderable/hideable overview columns (OTB is always first). Ids are slots,
// not metrics: `sdly` and `forecast` resolve through the data preferences.
export const OVERVIEW_COLUMNS = ['sdly', 'ly', 'forecast', 'budget'] as const;

export type OverviewColumn = (typeof OVERVIEW_COLUMNS)[number];

export function getOverviewColumnMetrics(
  preferences: DataPreferences,
): Record<OverviewColumn, MetricType> {
  return {
    sdly: preferences.sdlyColumn,
    ly: 'ly',
    forecast: preferences.forecastColumn,
    budget: 'budget',
  };
}

// Reorderable/hideable budget columns (OTB is always first).
export const BUDGET_COLUMNS = ['budget'] as const;

// The KPIs the budget view shows, in their default order.
export const BUDGET_KPIS: readonly Kpi[] = ['occupancy', 'adr', 'revenue'];

export type BudgetColumn = (typeof BUDGET_COLUMNS)[number];

export function getBudgetColumnMetrics(): Record<BudgetColumn, MetricType> {
  return { budget: 'budget' };
}

// Reorderable/hideable forecast columns (OTB is always first); each id is its metric.
export const FORECAST_VIEW_COLUMNS = [
  FORECAST_COLUMNS.BI,
  FORECAST_COLUMNS.RMS,
  FORECAST_COLUMNS.USER,
  FORECAST_COLUMNS.FINANCE,
] as const;

// The KPIs the forecast view shows, in their default order.
export const FORECAST_KPIS: readonly Kpi[] = ['occupancy', 'adr', 'revenue'];

export function getForecastColumnMetrics(): Record<ForecastColumn, MetricType> {
  return {
    bi_forecast: 'bi_forecast',
    rms_forecast: 'rms_forecast',
    user_forecast: 'user_forecast',
    finance_forecast: 'finance_forecast',
  };
}

// Reorderable/hideable pickup columns (OTB is always first); each id is its metric.
export const PICKUP_COLUMNS = [
  'otb:pickup:1',
  'bi_forecast:pickup:1',
  'otb:pickup:7',
  'bi_forecast:pickup:7',
] as const;

// The KPIs the pickup view shows, in their default order.
export const PICKUP_KPIS: readonly Kpi[] = [
  'occupancy',
  'rooms',
  'adr',
  'revenue',
];

export type PickupColumn = (typeof PICKUP_COLUMNS)[number];

export function getPickupColumnMetrics(): Record<PickupColumn, MetricType> {
  return {
    'otb:pickup:1': 'otb:pickup:1',
    'bi_forecast:pickup:1': 'bi_forecast:pickup:1',
    'otb:pickup:7': 'otb:pickup:7',
    'bi_forecast:pickup:7': 'bi_forecast:pickup:7',
  };
}

// Reorderable/hideable transient/group columns (OTB is always first). Ids are
// slots, not metrics: the SDLY ones resolve through the data preferences.
export const TRANSIENT_GROUP_COLUMNS = [
  'transient',
  'transient_sdly',
  'transient_ly',
  'group',
  'group_sdly',
  'group_ly',
] as const;

// The KPI cards of the transient/group view, in their default order.
export const TRANSIENT_GROUP_KPI_CARDS: readonly KpiCardName[] = [
  'transient_room_nights',
  'group_room_nights',
  'group_room_block',
];

export type TransientGroupColumn = (typeof TRANSIENT_GROUP_COLUMNS)[number];

export type Segment = 'group' | 'transient';

/** The metric type of a segment's column: `transient` for OTB, else `transient:sdly`, `group:ly`... */
export function segmentMetricType(
  segment: Segment,
  base: MetricType,
): MetricType {
  return (base === 'otb' ? segment : `${segment}:${base}`) as MetricType;
}

export function getTransientGroupColumnMetrics(
  preferences: DataPreferences,
): Record<TransientGroupColumn, MetricType> {
  const { sdlyColumn } = preferences;

  return {
    transient: segmentMetricType('transient', 'otb'),
    transient_sdly: segmentMetricType('transient', sdlyColumn),
    transient_ly: segmentMetricType('transient', 'ly'),
    group: segmentMetricType('group', 'otb'),
    group_sdly: segmentMetricType('group', sdlyColumn),
    group_ly: segmentMetricType('group', 'ly'),
  };
}

/** The sub-columns of a transient/group column: groups show OTB/BLK rooms and the blocked revenue. */
export function getTransientGroupKpis(metricType: MetricType): TableKpi[] {
  return metricType.startsWith('group')
    ? ['otb_blk', 'adr', 'revenue', 'rev_blk']
    : ['rooms', 'adr', 'revenue'];
}

export const KPI_TRANSLATIONS: Record<Kpi, string> = {
  adr: tKey('general.adr'),
  occupancy: tKey('market.snapshots.columns.occ'),
  revenue: tKey('general.revenue'),
  revpar: tKey('general.revpar'),
  rooms: tKey('market.snapshots.columns.rms'),
};

export const TABLE_KPI_TRANSLATIONS: Record<TableKpi, string> = {
  ...KPI_TRANSLATIONS,
  otb_blk: tKey('market.snapshots.columns.otb_blk'),
  rev_blk: tKey('market.snapshots.columns.rev_blk'),
};

// Snapshot views; the id is the URL slug and what the view switcher selects.
export const SNAPSHOT_VIEWS = [
  {
    id: 'overview',
    label: tKey('market.snapshots.view.overview'),
    route: 'market.snapshots.overview',
  },
  {
    id: 'forecast',
    label: tKey('market.snapshots.view.forecast'),
    route: 'market.snapshots.forecast',
  },
  {
    id: 'budget',
    label: tKey('market.snapshots.view.budget'),
    route: 'market.snapshots.budget',
  },
  {
    id: 'pickup',
    label: tKey('market.snapshots.view.pickup'),
    route: 'market.snapshots.pickup',
  },
  {
    id: 'transient-group',
    label: tKey('market.snapshots.view.transient_group'),
    route: 'market.snapshots.transient-group',
  },
] as const;

export type SnapshotView = (typeof SNAPSHOT_VIEWS)[number]['id'];

/** The reorderable/hideable columns of a view and where their settings are stored. */
export interface SnapshotViewColumnsConfig {
  /** Only categories can be managed: the Categories/KPIs switch is hidden and categories mode is forced. */
  categoriesOnly?: boolean;
  /** Always-first metric outside `ids`; can't be moved or hidden. Without it, one column must stay visible. */
  fixed?: MetricType;
  ids: readonly string[];
  /** The KPIs the view shows, in their default order. */
  kpis: readonly Kpi[];
  /** The sub-columns of each column, for views whose columns don't share the same KPIs. */
  kpisFor?: (metricType: MetricType) => readonly TableKpi[];
  /** Resolves column slots to the metrics they show for the given preferences. */
  metrics: (preferences: DataPreferences) => Record<string, MetricType>;
  storageKeys: { columns: StorageKey; kpiColumns: StorageKey };
}

/** Everything that differs between snapshot views; routes and controllers read the rest from here. */
export interface SnapshotViewConfig {
  api: {
    compare: (preferences: DataPreferences) => string;
    /** Pickup periods per metric (`pickup[otb]=1,7`). */
    pickup?: Record<string, string>;
    /** Adds the transient / group breakdown (`segments=true`). */
    segments?: boolean;
    view: string;
  };
  columns: SnapshotViewColumnsConfig;
  /** The access feature that gates the view's route and its entry in the view switcher. */
  feature: string;
  /** Whether the table shows variance; without it the Variance toggle is hidden. */
  hasVariance: boolean;
  id: SnapshotView;
  /** Whether the table can be sorted; unsortable views stay in `name` order. */
  isSortable: boolean;
  /** The KPI cards above the table, in order. */
  kpiCards: readonly KpiCardName[];
  /** What the KPI cards' variance compares against. */
  kpiComparedTo: (preferences: DataPreferences) => ComparisonMetricType;
}

// `satisfies` keeps each view's own (literal) shape.
export const SNAPSHOT_VIEW_CONFIG = {
  overview: {
    api: {
      compare: ({ forecastColumn, sdlyColumn }): string =>
        `${sdlyColumn},ly,${forecastColumn},budget`,
      view: 'overview',
    },
    columns: {
      fixed: 'otb',
      ids: OVERVIEW_COLUMNS,
      kpis: KPIS,
      metrics: getOverviewColumnMetrics,
      storageKeys: {
        columns: LOCAL_STORAGE_KEYS.MARKET_SNAPSHOTS_OVERVIEW_COLUMNS,
        kpiColumns: LOCAL_STORAGE_KEYS.MARKET_SNAPSHOTS_OVERVIEW_KPI_COLUMNS,
      },
    },
    feature: 'reservations.market.overview',
    hasVariance: true,
    id: 'overview',
    isSortable: true,
    kpiCards: OVERVIEW_KPI_NAMES,
    kpiComparedTo: ({ sdlyColumn }): ComparisonMetricType => sdlyColumn,
  },
  budget: {
    // SDLY only feeds the KPI cards; the table shows `columns.metrics`.
    api: {
      compare: ({ sdlyColumn }): string => `${sdlyColumn},budget`,
      view: 'budget',
    },
    columns: {
      fixed: 'otb',
      ids: BUDGET_COLUMNS,
      kpis: BUDGET_KPIS,
      metrics: getBudgetColumnMetrics,
      storageKeys: {
        columns: LOCAL_STORAGE_KEYS.MARKET_SNAPSHOTS_BUDGET_COLUMNS,
        kpiColumns: LOCAL_STORAGE_KEYS.MARKET_SNAPSHOTS_BUDGET_KPI_COLUMNS,
      },
    },
    feature: 'reservations.market.budget',
    hasVariance: true,
    id: 'budget',
    isSortable: true,
    kpiCards: BUDGET_KPIS,
    kpiComparedTo: ({ sdlyColumn }): ComparisonMetricType => sdlyColumn,
  },
  forecast: {
    // SDLY only feeds the KPI cards; the table shows `columns.metrics`.
    api: {
      compare: ({ sdlyColumn }): string =>
        [sdlyColumn, ...FORECAST_VIEW_COLUMNS].join(','),
      view: 'forecast',
    },
    columns: {
      fixed: 'otb',
      ids: FORECAST_VIEW_COLUMNS,
      kpis: FORECAST_KPIS,
      metrics: getForecastColumnMetrics,
      storageKeys: {
        columns: LOCAL_STORAGE_KEYS.MARKET_SNAPSHOTS_FORECAST_COLUMNS,
        kpiColumns: LOCAL_STORAGE_KEYS.MARKET_SNAPSHOTS_FORECAST_KPI_COLUMNS,
      },
    },
    feature: 'reservations.market.forecast',
    hasVariance: true,
    id: 'forecast',
    isSortable: true,
    kpiCards: FORECAST_KPIS,
    kpiComparedTo: ({ sdlyColumn }): ComparisonMetricType => sdlyColumn,
  },
  pickup: {
    // SDLY only feeds the KPI cards; BI forecast is the base of its pickups.
    api: {
      compare: ({ sdlyColumn }): string => `${sdlyColumn},bi_forecast`,
      pickup: { otb: '1,7', bi_forecast: '1,7' },
      view: 'pickup',
    },
    columns: {
      fixed: 'otb',
      ids: PICKUP_COLUMNS,
      kpis: PICKUP_KPIS,
      metrics: getPickupColumnMetrics,
      storageKeys: {
        columns: LOCAL_STORAGE_KEYS.MARKET_SNAPSHOTS_PICKUP_COLUMNS,
        kpiColumns: LOCAL_STORAGE_KEYS.MARKET_SNAPSHOTS_PICKUP_KPI_COLUMNS,
      },
    },
    feature: 'reservations.market.pickup',
    hasVariance: false,
    id: 'pickup',
    isSortable: false,
    kpiCards: PICKUP_KPIS,
    kpiComparedTo: ({ sdlyColumn }): ComparisonMetricType => sdlyColumn,
  },
  'transient-group': {
    // SDLY only feeds the KPI cards; the table shows `columns.metrics`.
    api: {
      compare: ({ sdlyColumn }): string => `${sdlyColumn},ly`,
      segments: true,
      view: 'transient_group',
    },
    columns: {
      categoriesOnly: true,
      fixed: 'otb',
      ids: TRANSIENT_GROUP_COLUMNS,
      kpis: ['rooms', 'adr', 'revenue'],
      kpisFor: getTransientGroupKpis,
      metrics: getTransientGroupColumnMetrics,
      storageKeys: {
        columns: LOCAL_STORAGE_KEYS.MARKET_SNAPSHOTS_TRANSIENT_GROUP_COLUMNS,
        kpiColumns:
          LOCAL_STORAGE_KEYS.MARKET_SNAPSHOTS_TRANSIENT_GROUP_KPI_COLUMNS,
      },
    },
    feature: 'reservations.market.transient_group',
    hasVariance: true,
    id: 'transient-group',
    isSortable: true,
    kpiCards: TRANSIENT_GROUP_KPI_CARDS,
    kpiComparedTo: ({ sdlyColumn }): ComparisonMetricType => sdlyColumn,
  },
} satisfies Record<SnapshotView, SnapshotViewConfig>;
